// src/components/admin/sections/AdminTestimonials.tsx - VERSIÓN COMPLETA FUNCIONAL
import React, { useState, useEffect, useRef } from 'react';
import apiElCristo, { Testimonial, TestimonialData } from '../../../service/api_elcristo';
import './AdminTestimonials.css';

const AdminTestimonials: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [testimonialToDelete, setTestimonialToDelete] = useState<{ testimonial: Testimonial, index: number } | null>(null);
  const [editingTestimonial, setEditingTestimonial] = useState<Testimonial | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  const titleEditorRef = useRef<HTMLDivElement>(null);

  const [testimonialData, setTestimonialData] = useState<TestimonialData>({
    subtitle: '',
    title: '',
    testimonials: []
  });

  const [formData, setFormData] = useState<Testimonial>({
    quote: '',
    author: '',
    quoteIcon: '',
    backgroundStyle: 'default'
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (titleEditorRef.current && testimonialData.title) {
      titleEditorRef.current.innerHTML = testimonialData.title;
    }
  }, [testimonialData.title]);

  const loadData = async () => {
    try {
      setLoading(true);
      const data = await apiElCristo.getTestimonialData();
      console.log('📥 Testimonials data:', data);
      setTestimonialData(data);
    } catch (error) {
      console.error('❌ Error loading:', error);
      setMessage({ type: 'error', text: 'Error al cargar comunicados' });
    } finally {
      setLoading(false);
    }
  };

  const handleSectionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setTestimonialData(prev => ({ ...prev, [name]: value }));
  };

  const handleTitleInput = () => {
    if (titleEditorRef.current) {
      setTestimonialData(prev => ({ ...prev, title: titleEditorRef.current!.innerHTML }));
    }
  };

  const applyFormat = (editorRef: React.RefObject<HTMLDivElement>, tag: string) => {
    if (!editorRef.current) return;

    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0);
    const selectedText = range.toString();

    if (selectedText) {
      const span = document.createElement('span');
      span.className = tag;
      span.textContent = selectedText;
      range.deleteContents();
      range.insertNode(span);
      
      if (editorRef === titleEditorRef) {
        setTestimonialData(prev => ({ ...prev, title: titleEditorRef.current!.innerHTML }));
      }
    }
  };

  const clearFormat = (editorRef: React.RefObject<HTMLDivElement>) => {
    if (!editorRef.current) return;
    
    const plainText = editorRef.current.innerText;
    editorRef.current.innerHTML = plainText;
    
    if (editorRef === titleEditorRef) {
      setTestimonialData(prev => ({ ...prev, title: plainText }));
    }
  };

  const handleOpenModal = (testimonial?: Testimonial, index?: number) => {
    if (testimonial && index !== undefined) {
      setEditingTestimonial(testimonial);
      setEditingIndex(index);
      setFormData({ ...testimonial });
    } else {
      setEditingTestimonial(null);
      setEditingIndex(null);
      setFormData({
        quote: '',
        author: '',
        quoteIcon: '/assets/kidsa/assets/img/quote.png',
        backgroundStyle: 'default'
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingTestimonial(null);
    setEditingIndex(null);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveTestimonial = async () => {
    if (!formData.quote || !formData.author) {
      setMessage({ type: 'error', text: '❌ Completa todos los campos' });
      setTimeout(() => setMessage(null), 3000);
      return;
    }

    setSaving(true);

    try {
      let updatedTestimonials;
      
      if (editingIndex !== null) {
        updatedTestimonials = [...testimonialData.testimonials];
        updatedTestimonials[editingIndex] = formData;
      } else {
        updatedTestimonials = [...testimonialData.testimonials, formData];
      }

      const updatedData = { ...testimonialData, testimonials: updatedTestimonials };
      
      console.log('📤 Guardando comunicado:', updatedData);
      
      await apiElCristo.updateSiteContent({ testimonial: updatedData });
      
      setTestimonialData(updatedData);
      handleCloseModal();
      
      setMessage({ type: 'success', text: '✅ Comunicado guardado' });
      setTimeout(() => setMessage(null), 3000);
      
    } catch (error: any) {
      console.error('❌ Error:', error);
      setMessage({ type: 'error', text: `❌ ${error.message || 'Error al guardar'}` });
      setTimeout(() => setMessage(null), 3000);
    } finally {
      setSaving(false);
    }
  };

  const handleOpenDeleteModal = (testimonial: Testimonial, index: number) => {
    setTestimonialToDelete({ testimonial, index });
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setTestimonialToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!testimonialToDelete) return;

    setSaving(true);

    try {
      const updatedTestimonials = testimonialData.testimonials.filter((_, i) => i !== testimonialToDelete.index);
      const updatedData = { ...testimonialData, testimonials: updatedTestimonials };
      
      console.log('📤 Eliminando comunicado:', updatedData);
      
      await apiElCristo.updateSiteContent({ testimonial: updatedData });
      
      setTestimonialData(updatedData);
      handleCloseDeleteModal();
      
      setMessage({ type: 'success', text: '✅ Comunicado eliminado' });
      setTimeout(() => setMessage(null), 3000);
      
    } catch (error: any) {
      console.error('❌ Error:', error);
      setMessage({ type: 'error', text: `❌ ${error.message}` });
      setTimeout(() => setMessage(null), 3000);
    } finally {
      setSaving(false);
    }
  };

  const handleMoveTestimonial = (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= testimonialData.testimonials.length) return;

    const testimonials = [...testimonialData.testimonials];
    [testimonials[index], testimonials[newIndex]] = [testimonials[newIndex], testimonials[index]];
    setTestimonialData(prev => ({ ...prev, testimonials }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);

    try {
      console.log('📤 Guardando cambios generales:', testimonialData);
      
      await apiElCristo.updateSiteContent({ testimonial: testimonialData });
      
      setMessage({ type: 'success', text: '✅ Cambios guardados' });
      setTimeout(() => setMessage(null), 3000);
      
      await loadData();
    } catch (error: any) {
      console.error('❌ Error:', error);
      setMessage({ type: 'error', text: `❌ ${error.message || 'Error al guardar'}` });
    } finally {
      setSaving(false);
    }
  };

  const getBgStyleClass = (style: string) => {
    switch (style) {
      case 'bg-2': return 'bg-style-2';
      case 'bg-3': return 'bg-style-3';
      default: return 'bg-style-default';
    }
  };

  if (loading) {
    return (
      <div className="admin-testimonials-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-testimonials-container">
      <div className="page-header">
        <div>
          <h1><i className="fas fa-quote-right"></i> Anuncios & Comunicados</h1>
          <p>Gestionar comunicados del colegio</p>
        </div>
        <button type="button" className="btn btn-primary" onClick={() => handleOpenModal()}>
          <i className="fas fa-plus"></i>
          Nuevo Comunicado
        </button>
      </div>

      {message && (
        <div className={`alert ${message.type === 'success' ? 'alert-success' : 'alert-error'}`}>
          <i className={`fas fa-${message.type === 'success' ? 'check' : 'exclamation'}-circle`}></i>
          {message.text}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="card">
          <div className="card-header">
            <h3><i className="fas fa-cog"></i> Configuración</h3>
          </div>
          <div className="card-body">
            <div className="form-group">
              <label><i className="fas fa-tag"></i> Subtítulo</label>
              <input
                type="text"
                name="subtitle"
                value={testimonialData.subtitle}
                onChange={handleSectionChange}
                placeholder="Ej: Comunicados"
                className="form-control"
              />
            </div>

            <div className="form-group">
              <label><i className="fas fa-heading"></i> Título</label>
              
              <div className="editor-toolbar">
                <button type="button" className="toolbar-btn" onClick={() => applyFormat(titleEditorRef, 'elcristo-primary')}>
                  <i className="fas fa-font" style={{ color: '#ff0f00' }}></i> Rojo
                </button>
                <button type="button" className="toolbar-btn" onClick={() => applyFormat(titleEditorRef, 'elcristo-blue')}>
                  <i className="fas fa-font" style={{ color: '#70A6B1' }}></i> Azul
                </button>
                <button type="button" className="toolbar-btn" onClick={() => clearFormat(titleEditorRef)}>
                  <i className="fas fa-eraser"></i> Limpiar
                </button>
              </div>

              <div
                ref={titleEditorRef}
                className="rich-editor"
                contentEditable
                onInput={handleTitleInput}
                suppressContentEditableWarning
                data-placeholder="Título de la sección"
              />
              <small className="hint">Usa saltos de línea para separar frases</small>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h3><i className="fas fa-list"></i> Comunicados ({testimonialData.testimonials.length})</h3>
          </div>
          <div className="card-body">
            {testimonialData.testimonials.length === 0 ? (
              <div className="empty-state">
                <i className="fas fa-quote-right"></i>
                <p>No hay comunicados</p>
                <button type="button" className="btn btn-primary" onClick={() => handleOpenModal()}>
                  <i className="fas fa-plus"></i>
                  Crear Comunicado
                </button>
              </div>
            ) : (
              <div className="testimonials-grid">
                {testimonialData.testimonials.map((testimonial, index) => (
                  <div key={index} className={`testimonial-card ${getBgStyleClass(testimonial.backgroundStyle || 'default')}`}>
                    <div className="testimonial-card-drag">
                      <i className="fas fa-grip-vertical"></i>
                    </div>
                    
                    <div className="quote-icon">
                      <img src={testimonial.quoteIcon || '/assets/kidsa/assets/img/quote.png'} alt="quote" />
                    </div>
                    
                    <div className="testimonial-content">
                      <p className="quote-text">"{testimonial.quote}"</p>
                      <h5 className="author-name">{testimonial.author}</h5>
                      <span className="style-badge">{testimonial.backgroundStyle || 'default'}</span>
                    </div>
                    
                    <div className="testimonial-card-actions">
                      <button type="button" className="btn-icon" onClick={() => handleMoveTestimonial(index, 'up')} disabled={index === 0}>
                        <i className="fas fa-arrow-up"></i>
                      </button>
                      <button type="button" className="btn-icon" onClick={() => handleMoveTestimonial(index, 'down')} disabled={index === testimonialData.testimonials.length - 1}>
                        <i className="fas fa-arrow-down"></i>
                      </button>
                      <button type="button" className="btn-icon btn-edit" onClick={() => handleOpenModal(testimonial, index)}>
                        <i className="fas fa-edit"></i>
                      </button>
                      <button type="button" className="btn-icon btn-delete" onClick={() => handleOpenDeleteModal(testimonial, index)}>
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="form-actions">
          <button type="button" className="btn btn-secondary" onClick={loadData} disabled={saving}>
            <i className="fas fa-undo"></i> Recargar
          </button>

          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? (
              <><div className="spinner-small"></div> Guardando...</>
            ) : (
              <><i className="fas fa-save"></i> Guardar Cambios</>
            )}
          </button>
        </div>
      </form>

      {/* PREVIEW SECTION */}
      <div className="testimonials-preview-section">
        <div className="testimonials-preview-header">
          <h3><i className="fas fa-eye"></i> Vista Previa</h3>
        </div>
        
        <div className="testimonials-preview">
          <div className="preview-section-title">
            <span className="preview-subtitle">{testimonialData.subtitle || 'Comunicados'}</span>
            <h2 
              className="preview-title"
              dangerouslySetInnerHTML={{ 
                __html: testimonialData.title || 'Lo que dicen los <span class="elcristo-primary">padres</span>' 
              }}
            />
          </div>

          <div className="preview-testimonials-slider">
            {(testimonialData.testimonials.length > 0 ? testimonialData.testimonials : [
              { quote: 'Excelente colegio, mis hijos están muy felices aquí.', author: 'María González - Madre', quoteIcon: '/assets/kidsa/assets/img/quote.png', backgroundStyle: 'default' },
              { quote: 'La mejor decisión educativa que hemos tomado.', author: 'Pedro Sánchez - Padre', quoteIcon: '/assets/kidsa/assets/img/quote.png', backgroundStyle: 'bg-2' },
              { quote: 'Excelente nivel académico y valores.', author: 'Ana Torres - Apoderada', quoteIcon: '/assets/kidsa/assets/img/quote.png', backgroundStyle: 'bg-3' }
            ]).map((testimonial, index) => (
              <div key={index} className={`preview-testimonial-item ${getBgStyleClass(testimonial.backgroundStyle || 'default')}`}>
                <div className="preview-quote-icon">
                  <img src={testimonial.quoteIcon || '/assets/kidsa/assets/img/quote.png'} alt="quote" />
                </div>
                <p className="preview-quote-text">"{testimonial.quote}"</p>
                <h6 className="preview-author-name">{testimonial.author}</h6>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* MODAL CREAR/EDITAR */}
      {showModal && (
        <div className="team-modal-overlay">
          <div className="team-modal-container" style={{ maxWidth: '700px' }}>
            <div className="team-modal-header">
              <h3>
                <i className="fas fa-quote-right"></i>
                {editingTestimonial ? 'Editar Comunicado' : 'Nuevo Comunicado'}
              </h3>
              <button type="button" className="team-modal-close" onClick={handleCloseModal} disabled={saving}>
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="team-modal-body">
              <div className="form-group">
                <label>Comunicado/Testimonio *</label>
                <textarea
                  name="quote"
                  value={formData.quote}
                  onChange={handleChange}
                  placeholder="Escribe el comunicado aquí..."
                  rows={5}
                  className="form-control"
                />
                <small className="hint">Texto que aparecerá en la tarjeta</small>
              </div>

              <div className="form-group">
                <label>Autor/Firma *</label>
                <input
                  type="text"
                  name="author"
                  value={formData.author}
                  onChange={handleChange}
                  placeholder="María González - Madre"
                  className="form-control"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Estilo Fondo *</label>
                  <select name="backgroundStyle" value={formData.backgroundStyle} onChange={handleChange} className="form-control">
                    <option value="default">Blanco</option>
                    <option value="bg-2">Color 2</option>
                    <option value="bg-3">Color 3</option>
                  </select>
                  <small className="hint">Variación de color</small>
                </div>

                <div className="form-group">
                  <label>Icono Comillas</label>
                  <input
                    type="text"
                    name="quoteIcon"
                    value={formData.quoteIcon || ''}
                    onChange={handleChange}
                    placeholder="/assets/kidsa/assets/img/quote.png"
                    className="form-control"
                  />
                  <small className="hint">Por defecto usa icono estándar</small>
                </div>
              </div>

              <div className="testimonial-preview-modal">
                <h4><i className="fas fa-eye"></i> Vista Previa:</h4>
                <div className={`preview-card-modal ${getBgStyleClass(formData.backgroundStyle || 'default')}`}>
                  <div className="preview-quote-icon-modal">
                    <img src={formData.quoteIcon || '/assets/kidsa/assets/img/quote.png'} alt="quote" />
                  </div>
                  <p className="preview-quote-modal">"{formData.quote || 'Tu comunicado aparecerá aquí...'}"</p>
                  <h6 className="preview-author-modal">{formData.author || 'Autor'}</h6>
                </div>
              </div>
            </div>

            <div className="team-modal-footer">
              <button type="button" className="btn btn-secondary" onClick={handleCloseModal} disabled={saving}>
                Cancelar
              </button>
              <button type="button" className="btn btn-primary" onClick={handleSaveTestimonial} disabled={saving}>
                {saving ? (
                  <><div className="spinner-small"></div> Guardando...</>
                ) : (
                  <>{editingTestimonial ? 'Actualizar' : 'Agregar'}</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL DELETE */}
      {showDeleteModal && testimonialToDelete && (
        <div className="team-modal-overlay">
          <div className="team-modal-container" style={{ maxWidth: '500px' }}>
            <div className="team-modal-header">
              <h3>
                <i className="fas fa-exclamation-triangle" style={{ color: '#ff0f00' }}></i>
                Confirmar Eliminación
              </h3>
              <button type="button" className="team-modal-close" onClick={handleCloseDeleteModal} disabled={saving}>
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="team-modal-body">
              <p style={{ fontSize: '16px', marginBottom: '20px' }}>
                ¿Eliminar comunicado de <strong>"{testimonialToDelete.testimonial.author}"</strong>?
              </p>
              <p style={{ fontSize: '14px', color: '#666' }}>
                No se puede deshacer.
              </p>
            </div>

            <div className="team-modal-footer">
              <button type="button" className="btn btn-secondary" onClick={handleCloseDeleteModal} disabled={saving}>
                Cancelar
              </button>
              <button type="button" className="btn btn-danger" onClick={handleConfirmDelete} disabled={saving}>
                {saving ? (
                  <><div className="spinner-small"></div> Eliminando...</>
                ) : (
                  <><i className="fas fa-trash"></i> Eliminar</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminTestimonials;